import React, { useState } from 'react';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import { Typography, Box, createStyles, makeStyles, Theme } from '@material-ui/core';

import { monochrome } from '../theme/styles';

interface OptionProps {
  value: string;
  title: string;
  icon?: React.ReactNode;
}

interface Props {
  options: OptionProps[];
  onChange: (arg0: any) => void;
  /** Optional controlled value. When provided, the toggle reflects it instead of its internal state. */
  value?: string;
  /** Render the selected segment in the primary colour instead of the default light/dark surface. */
  primaryActive?: boolean;
  disabled?: boolean;
}

const useStyles = makeStyles<Theme, { primaryActive?: boolean }>(theme =>
  createStyles({
    buttonGroup: {
      width: '100%',
      height: 'inherit',
      backgroundColor: theme.palette.type === 'dark' ? monochrome.dark : monochrome.mediumlight,
      borderRadius: '8px',
      '& .MuiToggleButton-root.Mui-selected': {
        backgroundColor: props =>
          props.primaryActive
            ? theme.palette.primary.main
            : theme.palette.type === 'dark'
            ? monochrome.darkest
            : monochrome.light,
        borderRadius: '8px !important',
        color: props =>
          props.primaryActive
            ? theme.palette.getContrastText(theme.palette.primary.main)
            : theme.palette.type === 'dark'
            ? monochrome.light
            : monochrome.dark,
      },
      '& .MuiToggleButton-root.Mui-selected:hover': {
        backgroundColor: props => (props.primaryActive ? theme.palette.primary.main : undefined),
      },
    },
    button: {
      width: '100%',
      borderRadius: '8px',
      textTransform: 'none',
      borderColor: 'transparent',
      color: theme.palette.type === 'dark' ? '#14142B' : '#fff',
      fontSize: '13px',
      '& .MuiTypography-body1': {
        fontWeight: 600,
        whiteSpace: 'nowrap',
      },
    },
  })
);

export const AppToggle = ({ options, onChange, value, primaryActive, disabled }: Props) => {
  const classes = useStyles({ primaryActive });
  const [selectedValue, setSelectedValue] = useState(options[0].value);
  const currentValue = value ?? selectedValue;

  const handleChange = (_event: React.MouseEvent<HTMLElement, MouseEvent>, newValue: string) => {
    if (newValue) {
      onChange(newValue);
      setSelectedValue(newValue);
    }
  };
  return (
    <ToggleButtonGroup
      value={currentValue}
      exclusive
      onChange={handleChange}
      className={classes.buttonGroup}
    >
      {options.map(item => (
        <ToggleButton
          value={item.value}
          aria-label={item.value}
          key={item.value}
          disabled={disabled}
          className={classes.button}
        >
          <Box display="flex" alignItems="center" gridGap={10}>
            {item.title && <Typography>{item.title}</Typography>}
            {item.icon && item.icon}
          </Box>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
